// import { Role } from "@prisma/client";

import BlurredPlaceholder from "@/app/(components)/generics/BlurredPlaceholder/BlurredPlaceholder";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import NextStepCard from "@/app/(components)/generics/cards/NextStepCard/NextStepCard";
import IconChecklist from "@/app/(components)/generics/Icons/IconChecklist";
import IconGift from "@/app/(components)/generics/Icons/IconGift";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
// import { useBrand } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { useFetchProjectRecommendations } from "@/app/hooks/fetchs/project/useFetchProjectRecommendations";
import { useOfferActions } from "@/app/hooks/useOfferActions";
import { ModalStore } from "@/app/stores/ModalStore";
import { OfferStore } from "@/app/stores/OfferStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import {
  AnalysisCategoryLayout,
  ProjectCallsRecommendationKind,
} from "@/app/types/call/CallAnalysis";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { PlaceholderRow } from "@/app/types/common/PlaceholderRow";
import { Limits } from "@/app/vars/Limits";
import AnalysisCategory from "../AnalysisCategory/AnalysisCategory";
import styles from "./AnalysisRecommendations.module.css";
import AnalysisRecommendationCard from "./components/AnalysisRecommendationCard/AnalysisRecommendationCard";

// import ExpertAuditCard from "./components/ExpertAuditCard/ExpertAuditCard";

type Props = DefaultProps & {
  kind: ProjectCallsRecommendationKind;
};

export default function AnalysisRecommendations(props: Props) {
  const T = useT();
  const { params, kind } = props;
  const { lang } = params;
  const openSignUp = ModalStore((state) => state.openSignUp);
  const user = useAuthUser();
  const isConnected = !!user;

  const project = ProjectStore((state) => state.currentProject);
  const offerActions = useOfferActions(params);
  const currentOfferId = OfferStore((state) => state.currentOfferId);
  const { data: recommendationsPayload, isLoading } = useFetchProjectRecommendations(
    project?.id,
    currentOfferId,
    kind,
  );
  const actions = recommendationsPayload?.actions || [];

  const handleConnect = () => {
    openSignUp(params);
  };

  const showAvatarCard = isConnected ? !isLoading && actions.length > 0 : true;

  const recoPlaceholderRows: PlaceholderRow[] = [
    { cells: [{ width: "40%" }, { width: "15%", pill: true }], withIcon: true },
    { cells: [{ width: "85%" }], withIcon: true },
    { cells: [{ width: "70%" }, { width: "10%", pill: true }], withIcon: true },
    { cells: [{ width: "20%" }], withIcon: false },
    { cells: [{ width: "90%" }], withIcon: true },
    { cells: [{ width: "60%" }, { width: "18%", pill: true }], withIcon: true },
  ];

  return (
    <>
      <AnalysisCategory
        params={params}
        icon={<IconChecklist />}
        colors={{ color: "var(--color-green-3)", bg: "var(--color-green-4)" }}
        titleKey={TKey.CALL_ANALYSIS_INSIGHT_ACTIONS}
        subtitleKey={TKey.CALL_ANALYSIS_INSIGHT_ACTIONS_SUB}
        layout={AnalysisCategoryLayout.SIDE}
      >
        <div className={styles.listWrapper}>
          <div className={styles.list}>
            {isConnected ? (
              isLoading ? (
                <PointsLoader
                  centered
                  color="var(--color-green-3)"
                  delay={Limits.CALL_ANALYSIS_LOADER_DELAY}
                />
              ) : (
                <>
                  <div className={styles.recommendationList}>
                    {actions.map((action, index) => (
                      <AnalysisRecommendationCard
                        key={index}
                        action={action}
                        index={index}
                        projectId={project!.id}
                        lang={lang}
                        kind={kind}
                      />
                    ))}
                  </div>
                  {/* {user?.role === Role.BUILDER && !isTenant && <ExpertAuditCard lang={lang} />} */}
                </>
              )
            ) : (
              <>
                <BlurredPlaceholder rows={recoPlaceholderRows} className={styles.placeholder} />
                <div className={styles.overlay}>
                  <Button
                    text={T(TKey.CALL_ANALYSIS_RECO_CONNECT)}
                    type={ButtonType.PRIMARY}
                    className={styles.cta}
                    onClick={handleConnect}
                    min={false}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </AnalysisCategory>

      {showAvatarCard && (
        <div className={styles.nextCard}>
          <NextStepCard
            params={params}
            icon={<IconGift />}
            title={T(TKey.CALL_ANALYSIS_NEXT_OFFER_TITLE) as string}
            description={T(TKey.CALL_ANALYSIS_NEXT_OFFER_DESCRIPTION) as string}
            ctaLabel={T(TKey.CALL_ANALYSIS_NEXT_OFFER_CTA) as string}
            onClick={offerActions.add}
          />
        </div>
      )}
    </>
  );
}
